<template>
  <v-container fluid class="pa-7">
    <header-component class="pb-4" :title="$vuetify.lang.t('$vuetify.navigation.search')"> </header-component>

    <v-alert v-if="$route.params.id" border="left" color="green" dark>
      {{ $vuetify.lang.t(`$vuetify.searching.success1`) }}
      <strong>
        {{ $route.params.id }}
      </strong>
      {{ $vuetify.lang.t(`$vuetify.searching.success2`) }}
    </v-alert>

    <v-row>
      <v-col cols="lg-4" offset-lg="4">
        <div class="relative d-flex">
          <v-text-field
            :label="$vuetify.lang.t('$vuetify.search.orderById')"
            type="number"
            outlined
            single-line
            hide-details
            v-model="searchQuery"
            @input="search"
            clearable
            :disabled="searchPending"
            ref="mainInput"
          />
          <v-progress-circular v-if="searchPending" indeterminate color="primary" class="input-loading my-3" />
        </div>

        <p v-if="isIncluded" class="color-red mt-2">{{ $vuetify.lang.t(`$vuetify.searching.selectedId`) }}</p>
        <p
          v-if="badStatus && !(this.items.length === 0 && this.status === REQUEST_STATUS.SUCCESS) && !isIncluded"
          class="color-red mt-2"
        >
          {{ $vuetify.lang.t(`$vuetify.searching.badStatus`) }}
        </p>
        <p v-if="this.items.length === 0 && this.status === REQUEST_STATUS.SUCCESS" class="color-red mt-2">
          {{ $vuetify.lang.t(`$vuetify.searching.badId`) }}
        </p>
        <div class="d-flex mt-4 w-100">
          <QrCodeReaderComponent @decode="decode($event)" :is-order="false" width="100%" height="56" class="w-100" />
        </div>

        <h4 v-if="!selectedOrders.length" class="text-uppercase mt-6 mb-3">
          {{ $vuetify.lang.t(`$vuetify.searching.todaySearched`) }}
        </h4>

        <v-row v-if="!selectedOrders.length">
          <v-col cols="6">
            <v-card
              elevation="2"
              class="text-center"
              :class="remaining && remaining.user.today_orders > 0 ? 'today-searched--green' : 'today-searched--orange'"
            >
              <div class="text-overline px-4 pt-2">
                {{ $vuetify.lang.t(`$vuetify.searching.orders`) }}
              </div>
              <v-card-title v-if="remaining && !remainingLoading" class="pt-0 justify-center">
                {{ remaining.user.today_orders }}
              </v-card-title>
              <v-progress-circular v-else indeterminate color="primary" :size="32" class="mb-4" />
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card
              elevation="2"
              class="text-center"
              :class="remaining && remaining.user.today_items > 0 ? 'today-searched--green' : 'today-searched--orange'"
            >
              <div class="text-overline px-4 pt-2">
                {{ $vuetify.lang.t(`$vuetify.searching.pieces`) }}
              </div>
              <v-card-title v-if="remaining && !remainingLoading" class="pt-0 justify-center">
                {{ remaining.user.today_items }}
              </v-card-title>
              <v-progress-circular v-else indeterminate color="primary" :size="32" class="mb-4" />
            </v-card>
          </v-col>
        </v-row>

        <h4 v-if="!selectedOrders.length" class="text-uppercase mt-6 mb-3">
          {{ $vuetify.lang.t(`$vuetify.searching.remains`) }}
        </h4>

        <v-row v-if="!selectedOrders.length">
          <v-col cols="6">
            <v-card elevation="2" class="text-center">
              <div class="text-overline px-4 pt-2">
                {{ $vuetify.lang.t(`$vuetify.searching.orders`) }}
              </div>
              <v-card-title v-if="remaining && !remainingLoading" class="pt-0 justify-center">
                {{ remaining.remaining_orders }}
              </v-card-title>
              <v-progress-circular v-else indeterminate color="primary" :size="32" class="mb-4" />
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card elevation="2" class="text-center">
              <div class="text-overline px-4 pt-2">
                {{ $vuetify.lang.t(`$vuetify.searching.pieces`) }}
              </div>
              <v-card-title v-if="remaining && !remainingLoading" class="pt-0 justify-center">
                {{ remaining.remaining_items }}
              </v-card-title>
              <v-progress-circular v-else indeterminate color="primary" :size="32" class="mb-4" />
            </v-card>
          </v-col>
        </v-row>

        <h4 v-show="selectedOrders.length" class="text-uppercase mt-6 mb-3">
          {{ $vuetify.lang.t(`$vuetify.searching.selected`) }}
        </h4>
        <v-card v-show="selectedOrders.length">
          <v-card-text class="pa-0">
            <div v-for="item in selectedOrders" :key="item.id">
              <v-container>
                <v-row class="align-center">
                  <div class="col-auto mr-auto">
                    <v-card-title class="color-black">
                      {{ item.id }}
                      <small class="pl-2 font-weight-regular grey--text">
                        ({{ item.subResources.items.map(item => item.amount).reduce((a, b) => a + b, 0) }} ks)
                      </small>

                      <v-chip
                        v-if="item.isFirstTimeCustomer"
                        class="font-weight-bold ml-2"
                        color="success"
                        size="large"
                        label
                      >
                        {{ $vuetify.lang.t('$vuetify.labels.newCustomer').toUpperCase() }}
                      </v-chip>
                    </v-card-title>
                    <v-card-subtitle>{{ _formatDate(item.createdAt) }}</v-card-subtitle>
                  </div>
                  <v-btn small color="transparent" class="col-auto action-button" @click="deleteItem(item.id)">
                    <v-icon color="red"> $vuetify.icons.delete </v-icon>
                  </v-btn>
                </v-row>
              </v-container>
              <v-divider />
            </div>
          </v-card-text>
        </v-card>

        <v-btn
          :disabled="!selectedOrders.length"
          @click="goToDetail()"
          color="green"
          :dark="selectedOrders.length !== 0"
          class="w-100 my-6"
          height="56"
        >
          <v-icon color="white" class="mr-4">$vuetify.icons.search</v-icon
          >{{ $vuetify.lang.t(`$vuetify.searching.next`) }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import QrCodeReaderComponent from '../../components/global/QrCodeReaderComponent.vue';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import moment from 'moment';
import { isFirstTimeCustomer } from '../../common/helpers/customer';

export default {
  name: 'SearchList',

  components: {
    HeaderComponent,
    QrCodeReaderComponent,
  },

  data() {
    return {
      REQUEST_STATUS,
      searchQuery: null,
      searchPending: false,
      selectedOrders: [],
      isIncluded: false,
      badStatus: false,
      remainingLoading: false,
    };
  },

  watch: {
    user() {
      this.fetchOrdersRemaining({ idData: { user_id: this.user.id } });
    },
  },

  computed: {
    ...mapGetters({
      user: 'auth/getUser',
      items: 'orders/getAllOrders',
      products: 'orders/getAllItems',
      remaining: 'orders/getRemaining',
      status: 'orders/getStatus',
    }),
  },

  methods: {
    ...mapActions({
      fetchAll: 'orders/fetchMaxOrders',
      fetchProducts: 'orders/fetchOrderItemsWithoutImages',
      fetchOrdersRemaining: 'orders/fetchOrdersSearchingRemaining',
    }),

    decode(e) {
      setTimeout(() => {
        this.searchQuery = e;
        this.$forceUpdate();
        this.search();
      }, 100);
    },

    deleteItem(id) {
      this.selectedOrders.splice(
        this.selectedOrders.findIndex(x => x.id === id),
        1
      );
    },

    goToDetail() {
      localStorage.setItem('myOrders', JSON.stringify(this.selectedOrders.map(order => order.id)));

      this.$router.push({ name: 'search.detail' });
    },

    async search() {
      if (this.searchQuery.length === 9 && !this.searchPending) {
        try {
          this.searchPending = true;
          this.isIncluded = false;
          this.badStatus = false;

          await this.fetchAll({
            searchQuery: this.searchQuery,
          });

          if (this.selectedOrders.map(x => x.id.toString()).includes(this.searchQuery)) {
            this.isIncluded = true;
          } else if (!this.items.map(x => x.attributes.order_state_id).includes(3)) {
            this.badStatus = true;
          } else {
            await this.fetchProducts(this.items[0].id);

            const isFirstTimeCustomerPromises = this.items.map(async el => {
              el.isFirstTimeCustomer = await isFirstTimeCustomer(el.id && el.customerId);
              return el;
            });

            const updatedItems = await Promise.all(isFirstTimeCustomerPromises);
            this.selectedOrders.push(...updatedItems);
          }
        } catch (error) {
          console.error("Error during search:", error);
        } finally {
          this.searchQuery = null;
          this.searchPending = false;

          this.$nextTick(() => {
            this.$refs.mainInput.focus();
          });
        }
      }
    },

    _formatDate(date) {
      return date ? moment(date).format('D. M. Y') : '';
    },
  },

  created() {
    if (this.user) {
      this.remainingLoading = true;

      this.fetchOrdersRemaining({ idData: { user_id: this.user.id } }).then(() => {
        this.remainingLoading = false;
      });
    }
  },

  mounted() {
    this.$refs.mainInput.focus();
  },
};
</script>

<style scoped>
  .today-searched--green {
    border-bottom: 0.3rem solid green;
  }

  .today-searched--orange {
    border-bottom: 0.3rem solid orange;
  }

  .input-loading {
    position: absolute;
    right: 1rem;
  }
</style>
